import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconPlayerSkipForwardFilled, IconX } from "@tabler/icons-react";

/**
 * AutoplayCountdown — "Próximo vídeo em Ns" end-of-video pill.
 *
 * Per-frame DOM updates bypass React's render cycle so the rest of the player
 * (and its children) is not re-rendered every animation frame.
 */
export default function AutoplayCountdown({ videoRef, countdownSeconds, onSkip, onCancel }) {
    const { t } = useTranslation("content");
    const progressRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        let rafId;

        const apply = () => {
            const video = videoRef?.current;
            if (video && video.duration && !isNaN(video.duration)) {
                const remaining = Math.max(0, video.duration - video.currentTime);
                const elapsed = countdownSeconds - remaining;
                const progress = Math.max(0, Math.min(1, elapsed / countdownSeconds));

                if (progressRef.current) {
                    progressRef.current.style.width = `${progress * 100}%`;
                }
                if (textRef.current) {
                    textRef.current.textContent = t("player.autoplay.nextIn", {
                        seconds: Math.max(0, Math.ceil(remaining)),
                    });
                }
            }
        };

        apply(); // initial sync to avoid a 1-frame flash of empty/zero state

        const tick = () => {
            apply();
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(rafId);
    }, [videoRef, countdownSeconds, t]);

    function handleCancelClick(event) {
        event.stopPropagation();
        onCancel?.();
    }

    return (
        <button
            type="button"
            className="autoplay-countdown"
            onClick={(event) => {
                event.stopPropagation();
                onSkip?.();
            }}
            aria-label={t("player.autoplay.skip")}
        >
            <span ref={progressRef} className="autoplay-countdown__progress" />

            <span className="autoplay-countdown__content">
                <IconPlayerSkipForwardFilled className="autoplay-countdown__icon" size={18} />
                <span ref={textRef} className="autoplay-countdown__text" />
            </span>

            <span
                className="autoplay-countdown__close"
                onClick={handleCancelClick}
                role="button"
                aria-label={t("player.autoplay.cancel")}
            >
                <IconX size={16} />
            </span>
        </button>
    );
}
